import { useEffect, useRef, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";
import { UTILITY_EVENT_TYPES } from "score-common";
import { formatVenueTime, isVenueTimezoneConfigured } from "common-fe/venueTime";
import { useRingData } from "../context/RingDataContext";
import { formatClockTime } from "../utils/formatClock";

// #3596 -- one active alert (Blood / Lameness / Meeting requested, the existing
// Ride.utilityEvents record) with its Dismiss button. Used in two places: the
// In the Ring Now panel (the ring ride's alert) and the list behind the
// "other rides" chip. Any staff screen can dismiss, and it ends everywhere.
//
// Solid backgrounds chosen for >= 4.5:1 against white text (MUI's stock filled
// warning orange is only ~3:1), so it stays legible on a bright ring-side tablet.
const TYPE_BG = {
  [UTILITY_EVENT_TYPES.BLOOD]: "#c62828",
  [UTILITY_EVENT_TYPES.LAMENESS]: "#b45309",
  [UTILITY_EVENT_TYPES.MEETING_REQUESTED]: "#01579b",
};

// Static i18n keys only (never t(`alerts.${type}`)).
export function alertTypeLabel(type, t) {
  switch (type) {
    case UTILITY_EVENT_TYPES.BLOOD:
      return t("alerts.typeBlood");
    case UTILITY_EVENT_TYPES.LAMENESS:
      return t("alerts.typeLameness");
    case UTILITY_EVENT_TYPES.MEETING_REQUESTED:
      return t("alerts.typeMeetingRequested");
    default:
      return String(type);
  }
}

export default function ActiveAlert({ row, event, showRide = false }) {
  const { t } = useTranslation("announcer");
  const { selectedShow, dismissAlert } = useRingData();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const zone = selectedShow?.venueTimezone;
  // raisedAt is a genuine UTC instant (server clock), so it follows the show's
  // venue zone when configured, else the tablet's own local time.
  const time = event.raisedAt
    ? isVenueTimezoneConfigured(zone)
      ? formatVenueTime(event.raisedAt, { timeZone: zone })
      : formatClockTime(event.raisedAt)
    : "";
  const name = event.raisedByName || t("alerts.unknownScribe");

  const onDismiss = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await dismissAlert(row.id, event.id);
    } catch {
      if (mounted.current) setFailed(true);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <Box
      data-testid="utility-alert"
      data-alert-type={event.type}
      sx={{
        color: "#fff",
        backgroundColor: TYPE_BG[event.type] ?? "#b45309",
        borderRadius: 1,
        px: 1.5,
        py: 0.5,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="baseline" useFlexGap sx={{ minWidth: 0 }}>
          <Typography component="span" fontWeight={800} data-testid="utility-alert-type">
            {alertTypeLabel(event.type, t)}
          </Typography>
          {showRide ? (
            <Typography component="span" data-testid="utility-alert-ride">
              {t("alerts.ride", {
                number: row.competitorNumber,
                rider: row.riderName,
                horse: row.horseName,
              })}
            </Typography>
          ) : null}
          <Typography component="span" variant="body2" data-testid="utility-alert-raised">
            {time ? t("alerts.raisedByAt", { name, time }) : t("alerts.raisedBy", { name })}
          </Typography>
        </Stack>
        <Button
          size="small"
          variant="outlined"
          data-testid="utility-alert-dismiss"
          disabled={busy}
          onClick={onDismiss}
          sx={{
            color: "#fff",
            borderColor: "#fff",
            flexShrink: 0,
            minHeight: 32,
            "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.15)" },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.8)", borderColor: "rgba(255,255,255,0.6)" },
          }}
        >
          {busy ? t("alerts.dismissing") : t("alerts.dismiss")}
        </Button>
      </Stack>
      {failed ? (
        <Typography role="alert" variant="body2" data-testid="utility-alert-dismiss-failed" sx={{ fontWeight: 700 }}>
          {t("alerts.dismissFailed")}
        </Typography>
      ) : null}
    </Box>
  );
}
